import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { hoursToString } from 'src/utils/datetime';
import { formatNumber } from 'src/utils/standards';
import { locale } from 'src/config';

import messages from './messages';
import CandidateInfo from '../CandidateInfo/CandidateInfo';
import type { CandidateTabProps } from '../CandidateShiftsTab';

function formatNumeric(param: number | null | undefined, unit = '') {
  const num = formatNumber(param, locale);
  return num ? `${num}${unit}` : '-';
}

export default function CandidateBehaviourTab({
  candidate,
}: CandidateTabProps) {
  const { formatMessage } = useIntl();

  return (
    <Grid container spacing={6} p={2}>
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.generalTitle)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.averageJobRating)}
          value={formatNumeric(candidate?.averageJobPerformanceRating)}
        />
        <CandidateInfo
          label={formatMessage(messages.annualCompletedShifts)}
          value={candidate?.workdaysCompleted?.toString() || '-'}
        />
        <CandidateInfo
          label={formatMessage(messages.annualWorkdayLeft)}
          value={candidate?.workdaysLeft?.toString() || '-'}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.applicationBehaviourTitle)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.applicationBookingRatio)}
          value={formatNumeric(candidate?.applicationsBookedRatio, '%')}
        />
        <CandidateInfo
          label={formatMessage(messages.jobConfirmationTime)}
          value={hoursToString(candidate?.confirmationTime) || '-'}
          capitalize={false}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.cancellationBehaviourTitle)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.bookingCancellationRatio)}
          value={formatNumeric(candidate?.applicationsCancelledRatio, '%')}
          tooltip={formatMessage(messages.bookingCancellationRatioTooltip)}
        />
        <CandidateInfo
          label={formatMessage(messages.timelyCancellationRatio)}
          value={formatNumeric(candidate?.timelyCancellationsRatio, '%')}
          tooltip={formatMessage(messages.timelyCancellationRatioTooltip)}
        />
        <CandidateInfo
          label={formatMessage(messages.justifiedCancellationRatio)}
          value={formatNumeric(candidate?.justifiedCancellationsRatio, '%')}
          tooltip={formatMessage(messages.justifiedCancellationRatioTooltip)}
        />
        <CandidateInfo
          label={formatMessage(messages.unjustifiedCancellationRatio)}
          value={formatNumeric(candidate?.unjustifiedCancellationsRatio, '%')}
          tooltip={formatMessage(messages.unjustifiedCancellationRatioTooltip)}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.jobBehaviourTitle)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.userInfringedDeviations)}
          value={formatNumeric(candidate?.punctualityRatio, '%')}
        />
      </Grid>
    </Grid>
  );
}
